import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';

const LoginForm = ({ onSuccess }) => {
  const { signIn } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '', rememberMe: false });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors?.[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/?.test(formData?.email)) newErrors.email = 'Please enter a valid email address';
    if (!formData?.password) newErrors.password = 'Password is required';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors)?.length > 0) { setErrors(newErrors); return; }
    setIsLoading(true);
    try {
      const { data, error } = await signIn(formData?.email, formData?.password);
      if (error) {
        setErrors({ general: error?.message || 'Invalid email or password' });
      } else if (data?.user) {
        onSuccess();
      }
    } catch (err) {
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e?.preventDefault();
    if (!resetEmail) return;
    setIsLoading(true);
    try {
      const { supabase } = await import('../../../lib/supabase');
      await supabase?.auth?.resetPasswordForEmail(resetEmail);
      setResetSent(true);
    } catch (err) {
      console.error('Reset error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <div className="space-y-6">
        {resetSent ? (
          <div className="text-center">
            <Icon name="CheckCircle" size={48} color="var(--color-success)" className="mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-text-primary mb-2">Check your email</h3>
            <p className="text-text-secondary">We sent a password reset link to {resetEmail}</p>
            <button onClick={() => { setShowForgotPassword(false); setResetSent(false); }} className="mt-4 text-primary hover:text-primary-700 transition-colors duration-200">
              Back to Sign In
            </button>
          </div>
        ) : (
          <div>
            <div className="text-center mb-6">
              <Icon name="Mail" size={48} color="var(--color-primary)" className="mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-text-primary mb-2">Reset your password</h3>
              <p className="text-text-secondary">Enter your email and we will send you a reset link.</p>
            </div>
            <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Email address</label>
                <input type="email" value={resetEmail} onChange={(e) => setResetEmail(e?.target?.value)} required
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  placeholder="Enter your email" />
              </div>
              <div className="space-y-3">
                <button type="submit" disabled={isLoading}
                  className="w-full bg-primary text-white py-3 px-4 rounded-lg hover:bg-primary-700 transition-colors duration-200 font-medium disabled:opacity-50">
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
                <button type="button" onClick={() => setShowForgotPassword(false)}
                  className="w-full text-text-secondary hover:text-text-primary transition-colors duration-200">
                  Back to Sign In
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errors?.general && (
        <div className="p-4 bg-error-50 border border-error-100 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} color="var(--color-error)" />
            <p className="text-sm text-error">{errors?.general}</p>
          </div>
        </div>
      )}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">Email address</label>
        <div className="relative">
          <input type="email" id="email" name="email" value={formData?.email} onChange={handleInputChange}
            className={`w-full px-4 py-3 pl-12 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 ${errors?.email ? 'border-error' : 'border-border'}`}
            placeholder="Enter your email" />
          <Icon name="Mail" size={20} color={errors?.email ? 'var(--color-error)' : 'var(--color-secondary-400)'} className="absolute left-4 top-1/2 transform -translate-y-1/2" />
        </div>
        {errors?.email && <p className="mt-1 text-sm text-error flex items-center space-x-1"><Icon name="AlertCircle" size={14} /><span>{errors?.email}</span></p>}
      </div>
      <div>
        <label htmlFor="password" className="block text-sm font-medium text-text-primary mb-2">Password</label>
        <div className="relative">
          <input type={showPassword ? 'text' : 'password'} id="password" name="password" value={formData?.password} onChange={handleInputChange}
            className={`w-full px-4 py-3 pl-12 pr-12 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 ${errors?.password ? 'border-error' : 'border-border'}`}
            placeholder="Enter your password" />
          <Icon name="Lock" size={20} color={errors?.password ? 'var(--color-error)' : 'var(--color-secondary-400)'} className="absolute left-4 top-1/2 transform -translate-y-1/2" />
          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-text-primary transition-colors duration-200">
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>
        {errors?.password && <p className="mt-1 text-sm text-error flex items-center space-x-1"><Icon name="AlertCircle" size={14} /><span>{errors?.password}</span></p>}
      </div>
      <div className="flex items-center justify-between">
        <label className="flex items-center space-x-2 cursor-pointer">
          <input type="checkbox" name="rememberMe" checked={formData?.rememberMe} onChange={handleInputChange} className="w-4 h-4 text-primary border-border rounded focus:ring-primary-500" />
          <span className="text-sm text-text-secondary">Remember me</span>
        </label>
        <button type="button" onClick={() => setShowForgotPassword(true)} className="text-sm text-primary hover:text-primary-700 transition-colors duration-200">
          Forgot password?
        </button>
      </div>
      <button type="submit" disabled={isLoading}
        className="w-full bg-primary text-white py-3 px-4 rounded-lg hover:bg-primary-700 transition-colors duration-200 font-medium flex items-center justify-center space-x-2 disabled:opacity-50">
        {isLoading ? (
          <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /><span>Signing in...</span></>
        ) : (
          <><Icon name="LogIn" size={20} /><span>Sign In</span></>
        )}
      </button>
      <div className="p-3 bg-secondary-50 border border-border rounded-lg">
        <p className="text-xs text-text-secondary text-center">
          <strong>Demo Credentials:</strong><br />
          admin@arkdai.com / password123<br />
          sarah@arkdai.com / password123
        </p>
      </div>
    </form>
  );
};

export default LoginForm;